import { Button, BUTTON_TYPES } from '../../../components/Button'
import { BTN_TXT, LABELS, PLACEHOLDERS, TOOLTIP } from '../../../constants'
import { DashboardFilterSelect } from './DashboardFilterSelect'
import { Popup } from '../../../components/Popup'
import { useDashboardPopup } from '../Providers/DashboardPopupProvider'
import { useCallback, useEffect, useMemo } from 'react'
import {
  filtersInitialState,
  useDashboardFilterContext
} from '../Providers/DashboardFilterContendProvider'
import { Form } from 'antd'
import { TDashboardFilters } from '../types'
import { useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'

export const FiltersModal = () => {
  const {
    filtersPopup: { state, actions }
  } = useDashboardPopup()

  const user = useSelector(getUser)

  const {
    state: {
      form,
      filters,
      hsOptions,
      hospitalOptions,
      userOptions,
      portfolioOptions,
      ccOptions,
      ledByOptions,
      canSeeHealthSystemsFilter,
      canSeeHospitalsFilter,
      canSeeLedByFilter,
      canSeePortfolioFilter,
      canSeeCcFilter,
      canSeeUserFilter,
      disableCategoriesSelect,
      disableHospitalsSelect,
      disableUserSelect,
      canSeeHealthSystemsFilterMultiSelect,
      canSeeHospitalsFilterMultiSelect,
      canSeePortfolioFilterMultiSelect,
      canSeeCcFilterMultiSelect,
      canSeeLedByFilterMultiSelect,
      canSeeUserFilterMultiSelect,
      canSeeMyHealthSystemLabel
    },
    actions: {
      resetFilters,
      onHSChange,
      onPortfolioChange,
      applyFilters,
      onCancelUpdateForm
    }
  } = useDashboardFilterContext()

  const onOk = useCallback(() => {
    const filters = form.getFieldsValue()
    applyFilters(filters)
    actions.close()
  }, [form, actions, applyFilters])

  const onReset = useCallback(() => {
    resetFilters()
    form.setFieldsValue(filtersInitialState)
    actions.close()
  }, [form, actions, resetFilters])

  useEffect(() => {
    form.setFieldsValue(filters)
  }, [filters, form])

  const onCancel = useCallback(() => {
    onCancelUpdateForm()
    actions.close()
  }, [actions, onCancelUpdateForm])

  const hsOptionsFormatted = useMemo(() => {
    return hsOptions.map((i) => ({
      ...i,
      chipsLabel:
        canSeeMyHealthSystemLabel && i.value === user.health_system
          ? 'My Health System'
          : undefined
    }))
  }, [hsOptions, canSeeMyHealthSystemLabel, user.health_system])

  return (
    <Popup
      {...state}
      onCancel={onCancel}
      className="check-ins__filter-modal"
      width={440}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={onReset} upperCase>
            {BTN_TXT.RESET_FILTERS}
          </Button>
          <Button type={BUTTON_TYPES.PRIMARY} onClick={onOk} upperCase>
            {BTN_TXT.APPLY}
          </Button>
        </>
      }
    >
      <Form<TDashboardFilters> form={form} initialValues={filtersInitialState}>
        <div className="flex gap-12 column">
          {canSeeHealthSystemsFilter && (
            <DashboardFilterSelect
              isMulti={canSeeHealthSystemsFilterMultiSelect}
              options={hsOptionsFormatted}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.health_systems,
                onChange: (v) => onHSChange(v, { updateForm: true }),
                placeholder: PLACEHOLDERS.ALL
              }}
              propsItem={{
                name: 'health_systems',
                label: LABELS.HEALTH_SYSTEMS
              }}
            />
          )}
          {canSeeHospitalsFilter && (
            <DashboardFilterSelect
              isMulti={canSeeHospitalsFilterMultiSelect}
              options={hospitalOptions}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.hospitals,
                placeholder: PLACEHOLDERS.ALL,
                disabled: disableHospitalsSelect
              }}
              propsItem={{
                name: 'hospitals',
                label: LABELS.HOSPITAL,
                tooltip: disableHospitalsSelect
                  ? TOOLTIP.HOSPITALS_DISABLED_ON_DASHBOARD_FILTER
                  : undefined
              }}
            />
          )}
          {canSeeLedByFilter && (
            <DashboardFilterSelect
              isMulti={canSeeLedByFilterMultiSelect}
              options={ledByOptions}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.led_by,
                placeholder: PLACEHOLDERS.ALL
              }}
              propsItem={{
                name: 'led_by',
                label: LABELS.LED_BY
              }}
            />
          )}
          {canSeeUserFilter && (
            <DashboardFilterSelect
              isMulti={canSeeUserFilterMultiSelect}
              options={userOptions}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.user,
                placeholder: PLACEHOLDERS.ALL,
                disabled: disableUserSelect
              }}
              propsItem={{
                name: 'user',
                label: LABELS.USER,
                tooltip: disableUserSelect
                  ? TOOLTIP.USER_DISABLED_ON_DASHBOARD_FILTER
                  : undefined
              }}
            />
          )}
          {canSeePortfolioFilter && (
            <DashboardFilterSelect
              isMulti={canSeePortfolioFilterMultiSelect}
              options={portfolioOptions}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.portfolio,
                onChange: (v) => onPortfolioChange(v, { updateForm: true }),
                placeholder: PLACEHOLDERS.ALL
              }}
              propsItem={{
                name: 'portfolio',
                label: LABELS.PORTFOLIO
              }}
            />
          )}
          {canSeeCcFilter && (
            <DashboardFilterSelect
              isMulti={canSeeCcFilterMultiSelect}
              options={ccOptions}
              propsSelect={{
                defaultValue: undefined,
                allowClear: true,
                value: filters.contract_categories,
                placeholder: PLACEHOLDERS.ALL,
                disabled: disableCategoriesSelect
              }}
              propsItem={{
                name: 'contract_categories',
                label: LABELS.CATEGORY,
                tooltip: disableCategoriesSelect
                  ? TOOLTIP.CATEGORIES_DISABLED_ON_DASHBOARD_FILTER
                  : undefined
              }}
            />
          )}
        </div>
      </Form>
    </Popup>
  )
}
